// Row-model emitter for the SMJ statement renderer (spec Part 3).
//
// This file computes NOTHING. It reads the objects the existing report
// builders already produce (buildBalanceSheet / buildIncomeStatement /
// buildCashFlow / buildT12) and re-expresses them as the renderer's row
// model, declaring each total's children in `of[]` so the server-side gate
// can walk the tree. Mapping, naming and client configuration are untouched.

// Kept in step with RENDERER_VERSION in the backend package, so any refusal
// screenshot identifies the build that produced it.
export const EMITTER_VERSION = '25.26';

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June',
                     'July', 'August', 'September', 'October', 'November',
                     'December'];

const PCT = (v) => {
  if (v === null || v === undefined || !Number.isFinite(v)) return '\u2014';
  const s = `${Math.abs(v).toFixed(1)}%`;
  return v < 0 ? `(${s})` : s;
};

// V25.17 — a percentage of revenue that rounds to nothing prints nothing: a
// column of 0.0% carries no information.
const PCT_REV = (v, base) => {
  if (!base || !Number.isFinite(v)) return '';
  const p = (v / base) * 100;
  return Math.abs(p) < 0.05 ? '' : PCT(p);
};

// V25.17 — `n/m` (not meaningful) for a percentage change off a zero or
// negative base, a line that went to zero, or a result past the conventional
// 999% column cap. Whole percents only — a decimal is false precision here.
// MUST stay in step with reconcile._pct_change on the server, which rebuilds
// this column after rounding reconciliation.
export const PCT_CHANGE = (current, base) => {
  if (!base || base < 0 || !current) return 'n/m';
  const p = ((current - base) / Math.abs(base)) * 100;
  if (Math.abs(p) > 999) return 'n/m';
  const s = `${Math.abs(p).toFixed(0)}%`;
  return p < 0 ? `(${s})` : s;
};

const T = titleizeLabel;

// Local-time ISO day. `toISOString()` would shift a midnight date back a day
// in any western timezone, which is how a July package would be stamped June.
const isoDay = (d) => (d instanceof Date && !Number.isNaN(d.getTime())
  ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  : null);

// V25.19 — not-for-profit terminology. The package is emitted in the
// for-profit wording and re-worded in ONE pass at the end (see
// applyNonprofitWording), so a rename can never desynchronize a total's
// declared children, a tie's operands, or a TTM formula.
const NP_EXACT = {
  Revenue: 'Support and revenue',
  'Total revenue': 'Total support and revenue',
  'Net income (loss)': 'Change in net assets',
  'Net income': 'Change in net assets',
  'Current year earnings': 'Change in net assets',
  'Income (loss) from operations': 'Change in net assets from operations',
};

const NP_PARTS = [
  [/Total liabilities and stockholders' equity/g, 'Total liabilities and net assets'],
  [/Total stockholders' equity/g, 'Total net assets'],
  [/Stockholders' equity/g, 'Net assets'],
  [/stockholders' equity/g, 'net assets'],
  [/Retained Earnings/g, 'Net Assets Without Donor Restrictions'],
  [/Retained earnings/g, 'Net assets without donor restrictions'],
  [/Net income \(loss\)/g, 'Change in net assets'],
  [/Net income/g, 'Change in net assets'],
  [/Net loss/g, 'Change in net assets'],
  [/Current [Yy]ear [Ee]arnings/g, 'Change in net assets'],
  [/Balance Sheet/g, 'Statement of Financial Position'],
  [/Balance sheet/g, 'Statement of financial position'],
  [/Statement of Income/g, 'Statement of Activities'],
  [/Statement of income/g, 'Statement of activities'],
];

const npLabel = (s) => {
  if (!s || typeof s !== 'string') return s;
  if (NP_EXACT[s]) return NP_EXACT[s];
  return NP_PARTS.reduce((out, [re, to]) => out.replace(re, to), s);
};

export function applyNonprofitWording(payload) {
  const L = npLabel;
  const ofEntry = (x) => (typeof x === 'string' ? L(x) : { ...x, label: L(x.label) });
  const stream = (f) => ({
    ...f,
    label: L(f.label),
    ...(f.add ? { add: f.add.map(L) } : {}),
    ...(f.subtract ? { subtract: f.subtract.map(L) } : {}),
    ...(f.percent_of ? { percent_of: { of: L(f.percent_of.of),
                                       base: (f.percent_of.base || []).map(L) } } : {}),
  });
  return {
    ...payload,
    statements: payload.statements.map((s) => ({
      ...s, title: L(s.title), toc: L(s.toc),
      rows: s.rows.map((r) => ({ ...r, label: L(r.label),
                                 ...(r.of ? { of: r.of.map(ofEntry) } : {}) })),
    })),
    ttm: payload.ttm ? {
      ...payload.ttm, title: L(payload.ttm.title), toc: L(payload.ttm.toc),
      rows: payload.ttm.rows.map((r) => ({ ...r, label: L(r.label) })),
      controls: Object.fromEntries(
        Object.entries(payload.ttm.controls || {}).map(([k, v]) => [L(k), v])),
      formulas: (payload.ttm.formulas || []).map(stream),
      ...(payload.ttm.net_income_of
        ? { net_income_of: stream(payload.ttm.net_income_of) } : {}),
    } : payload.ttm,
    ties: (payload.ties || []).map((t) => ({
      ...t, label: L(t.label),
      left: { ...t.left, label: L(t.left.label) },
      right: { ...t.right, label: L(t.right.label) },
    })),
    equity_checks: (payload.equity_checks || []).map((e) => ({
      ...e, total_label: L(e.total_label),
      columns: (e.columns || []).map((c) => ({
        ...c,
        ...(c.net_income
          ? { net_income: { ...c.net_income, label: L(c.net_income.label) } } : {}),
      })),
    })),
  };
}

const uniqueLabels = (rows) => {
  const seen = new Map();
  return rows.map((r) => {
    if (!r.label || r.k === 'section' || r.k === 'group') return r;
    const n = (seen.get(r.label) || 0) + 1;
    seen.set(r.label, n);
    return n === 1 ? r : { ...r, label: `${r.label} (${n})` };
  });
};

const hasLabel = (rows, label) => rows.some((r) => r.label === label && r.v);

// V25.10 #2 — suppression helpers. All decisions are made on DISPLAYED values
// across ALL columns shown on that statement, so the same classification can
// vanish from the single-period statement and stay on the comparative.
const allZeroCols = (arr) => (arr || []).every(
  (v) => typeof v !== 'number' || Math.round(v) === 0);

// #2a — a heading with nothing under it (after every other suppression) is
// noise. Runs on the portrait rows and on the TTM rows alike.
export function dropEmptySections(rows, kindKey = 'k') {
  const out = [];
  for (let i = 0; i < rows.length; i += 1) {
    const r = rows[i];
    if (r[kindKey] !== 'section' && r[kindKey] !== 'group') { out.push(r); continue; }
    let hasContent = false;
    for (let j = i + 1; j < rows.length; j += 1) {
      const n = rows[j];
      if (n[kindKey] === 'section') break;
      if (n[kindKey] === 'group' && r[kindKey] === 'section') continue;
      if (n[kindKey] === 'group') break;
      hasContent = true;
      break;
    }
    if (hasContent) out.push(r);
  }
  return out;
}

// ---------------------------------------------------------------- balance sheet
// `col` builds the column array for a line from its {current, prior} pair.
// V25.17 — printed labels are capitalized consistently. QuickBooks account
// names arrive however the bookkeeper typed them ("credit card fees",
// "publishing expense") and sat next to properly cased ones on the same page.
//
// The rule is deliberately one-directional: letters are only ever RAISED,
// never lowered. So an acronym (AICPA, LLC, AR), a brand (Bill.com,
// QuickBooks) and a deliberately lowercase small word ("Cost of Goods Sold")
// all survive untouched, while a lowercase word gets its initial capital.
// Nothing is written back to QuickBooks or to the mapping — this is a
// presentation pass over the payload only.
const SMALL_WORDS = new Set(['a', 'an', 'and', 'as', 'at', 'but', 'by', 'for',
                             'from', 'in', 'into', 'nor', 'of', 'on', 'or',
                             'per', 'the', 'to', 'vs', 'via', 'with']);

// A leading digit or symbol run means the letter is not the start of a word —
// "401(k)" must not become "401(K)".
const capFirst = (s) => s.replace(/^(["'(\u2018\u201c]?)([a-z])/,
                                  (_m, lead, ch) => lead + ch.toUpperCase());

export function titleizeLabel(label, keepLeadingSmallWord = false) {
  if (!label || typeof label !== 'string') return label;
  const parts = label.split(/(\s+)/);
  const words = parts.filter((w) => !/^\s*$/.test(w));
  let seen = 0;
  return parts.map((w) => {
    if (/^\s*$/.test(w)) return w;
    seen += 1;
    // an acronym or a deliberate mixed case carries its own intent
    if (/[A-Z]/.test(w.slice(1))) return w;
    const bare = w.replace(/[^A-Za-z]/g, '').toLowerCase();
    const inner = seen !== words.length && (seen !== 1 || keepLeadingSmallWord);
    if (inner && SMALL_WORDS.has(bare)) return w;
    return w.split(/([-/])/).map(capFirst).join('');
  }).join('');
}

// A cash-flow line is the builder's own prose wrapped around an account name
// ("Increase (Decrease) in credit card fees"). Only the account name — the
// part after the builder's parenthetical — is re-cased, so the canonical
// wording of the statement is untouched.
const cfLabel = (label) => {
  const i = (label || '').lastIndexOf(')');
  if (i < 0 || i === label.length - 1) return label;
  return label.slice(0, i + 1) + titleizeLabel(label.slice(i + 1), true);
};

// V25.17 — a line that is zero in EVERY column shown on that statement is not
// information. #2c suppressed a whole classification and #2d the cash flow,
// but an individual account line printed an em dash forever (MEET Ministry:
// "Clothing — 0.0% — 0.0%"). Column-aware by construction: the same account
// drops from the single-period statement and survives on the comparative,
// where its prior-year figure is real.
//
// Two safety rules, because the gate walks `of[]`:
//   • a total that carries a figure must keep at least one declared child, so
//     its children are kept if they would ALL drop;
//   • a subtotal left with no children AND no figure comes off with them, and
//     out of its parent's `of[]`.
export function dropZeroLines(rows) {
  const name = (k) => (typeof k === 'string' ? k : k.label);
  const drop = new Set(rows
    .filter((r) => r.k === 'item' && r.v && allZeroCols(r.v))
    .map((r) => r.label));
  if (!drop.size) return rows;

  for (let pass = 0; pass < 8; pass += 1) {
    let changed = false;
    rows.forEach((r) => {
      const kids = r.of || [];
      if (!kids.length) return;
      if (kids.some((k) => !drop.has(name(k)))) return;
      if (allZeroCols(r.v)) {
        // an empty, zero subtotal is noise too — but a grand total always prints
        if (r.k !== 'grand' && !drop.has(r.label)) { drop.add(r.label); changed = true; }
      } else {
        kids.forEach((k) => { if (drop.delete(name(k))) changed = true; });
      }
    });
    if (!changed) break;
  }

  return rows
    .filter((r) => !(r.v && r.k !== 'grand' && drop.has(r.label)))
    .map((r) => (r.of ? { ...r, of: r.of.filter((k) => !drop.has(name(k))) } : r));
}

export function bsRows(bs, niLabel, col) {
  const rows = [];
  const push = (r) => rows.push(r);

  const emitItems = (items, ownerKids) => {
    (items || []).forEach((it) => {
      if (it.kind === 'group') {
        const leaves = it.leaves || [];
        if (!leaves.length) return;
        push({ k: 'group', label: T(it.label), level: 3 });
        leaves.forEach((l) => push({ k: 'item', label: T(l.label), level: 4,
                                     v: col(l) }));
        const subLabel = `Total ${it.label.toLowerCase()}`;
        push({ k: 'subtotal', label: subLabel, level: 3, subgroup: true,
               v: col({ current: it.subtotal, prior: it.subtotal_prior }),
               of: leaves.map((l) => T(l.label)) });
        ownerKids.push(subLabel);
      } else {
        push({ k: 'item', label: T(it.label), level: 3, v: col(it) });
        ownerKids.push(T(it.label));
      }
    });
  };

  // A classification: heading, its items, and its own total.
  const classification = (heading, totalLabel, sections) => {
    const items = sections.flatMap((s) => s?.items || []);
    const total = sections.reduce((sum, s) => sum + (s?.total || 0), 0);
    const totalPrior = sections.reduce((sum, s) => sum + (s?.total_prior || 0), 0);
    // #2c — suppress a classification only when every account AND its total
    // are zero in every DISPLAYED column.
    const itemsZero = (items || []).every((it) => (it.kind === 'group'
      ? (it.leaves || []).every((l) => allZeroCols(col(l)))
        && allZeroCols(col({ current: it.subtotal, prior: it.subtotal_prior }))
      : allZeroCols(col(it))));
    if (allZeroCols(col({ current: total, prior: totalPrior })) && itemsZero) return null;
    push({ k: 'group', label: heading, level: 2 });
    const kids = [];
    emitItems(items, kids);
    push({ k: 'subtotal', label: totalLabel, level: 2,
           v: col({ current: total, prior: totalPrior }), of: kids });
    return totalLabel;
  };

  const S = bs.sections;
  push({ k: 'section', label: 'Assets' });
  const assetKids = [];
  // buildBalanceSheet already promotes the `cash` section into a synthetic
  // "Cash & Equivalents" group at the head of assetsCur.items (legacy bridge),
  // so it must NOT be added again here — that double-counted cash. The
  // classification total is the builder's combined figure.
  const currentAssetSections = [{
    items: S.assetsCur?.items || [],
    total: S.assetsCur?.totalCombined
      ?? ((S.cash?.total || 0) + (S.assetsCur?.total || 0)),
    total_prior: S.assetsCur?.totalCombined_prior
      ?? ((S.cash?.total_prior || 0) + (S.assetsCur?.total_prior || 0)),
  }];
  [
    classification('Current assets', 'Total current assets', currentAssetSections),
    classification('Property & equipment', 'Total property & equipment', [S.assetsFix]),
    classification('Other assets', 'Total other assets', [S.assetsOther]),
  ].forEach((t) => { if (t) assetKids.push(t); });
  push({ k: 'grand', label: 'Total assets', level: 1, strength: 'strong',
         v: col({ current: bs.totals.totalAssets, prior: bs.prior.totalAssets }),
         of: assetKids });

  push({ k: 'section', label: "Liabilities and stockholders' equity" });
  const liabKids = [];
  [
    classification('Current liabilities', 'Total current liabilities', [S.liabCur]),
    classification('Short-term debt', 'Total short-term debt', [S.debtShort]),
    classification('Long-term liabilities', 'Total long-term liabilities', [S.liabLong]),
  ].forEach((t) => { if (t) liabKids.push(t); });
  push({ k: 'total', label: 'Total liabilities', level: 1,
         v: col({ current: bs.totals.totalLiab, prior: bs.prior.totalLiab }),
         of: liabKids });

  // Stockholders' equity — role tags drive the derived roll-forward check.
  push({ k: 'group', label: "Stockholders' equity", level: 2 });
  const equityKids = [];
  (S.equity?.items || []).forEach((it) => {
    const leaves = it.kind === 'group' ? (it.leaves || []) : [it];
    leaves.forEach((l) => {
      // `_prior` never reaches the renderer (the payload parser whitelists
      // row keys); it is here so the tie builder can net out an opening
      // balance that was never closed to retained earnings.
      push({ k: 'item', label: T(l.label), level: 3, v: col(l),
             role: equityRole(l.label), _prior: l.prior || 0 });
      equityKids.push(T(l.label));
    });
  });
  push({ k: 'item', label: niLabel, level: 3, role: 'earnings',
         v: col({ current: bs.totals.netIncome, prior: bs.prior.netIncome }) });
  equityKids.push(niLabel);
  push({ k: 'subtotal', label: "Total stockholders' equity", level: 2, of: equityKids,
         v: col({ current: bs.totals.totalEquity, prior: bs.prior.totalEquity }) });
  push({ k: 'grand', label: "Total liabilities and stockholders' equity", level: 1,
         strength: 'final', of: ['Total liabilities', "Total stockholders' equity"],
         v: col({ current: bs.totals.totalLE, prior: bs.prior.totalLE }) });

  return uniqueLabels(rows);
}

function equityRole(label) {
  const l = (label || '').toLowerCase();
  if (/retained/.test(l)) return 'retained';
  if (/draw|distribut|dividend/.test(l)) return 'distributions';
  if (/contribut/.test(l)) return 'contributions';
  return 'other';
}

// ---------------------------------------------------------------- income
const IS_SECTIONS = [
  { key: 'income', heading: 'Revenue', total: 'Total revenue', sign: 1 },
  { key: 'cogs', heading: 'Cost of revenue', total: 'Total cost of revenue', sign: 1 },
  { key: 'opex', heading: 'Operating expenses', total: 'Total operating expenses', sign: 1 },
];

export function isRows(is, col, niLabel) {
  const rows = [];
  const emit = (section, heading, totalLabel, totals) => {
    const items = section?.items || [];
    if (!items.length && !section?.total) return null;
    rows.push({ k: 'section', label: heading });
    const kids = [];
    items.forEach((it) => {
      const leaves = it.kind === 'group' ? (it.leaves || []) : [it];
      leaves.forEach((l) => {
        rows.push({ k: 'item', label: T(l.label), level: 4, v: col(l) });
        kids.push(T(l.label));
      });
    });
    rows.push({ k: 'total', label: totalLabel, level: 1, of: kids, v: col(totals) });
    return totalLabel;
  };

  const revenueLabel = emit(is.sections.income, 'Revenue', 'Total revenue',
                            { current: is.totals.totalRevenue,
                              prior: is.prior.totalRevenue });

  // #2b — with no cost of revenue there is no gross margin to report, so the
  // section AND the gross profit line both come off. Revenue is followed
  // directly by operating expenses.
  const cogsCols = col({ current: is.totals.totalCOGS, prior: is.prior.totalCOGS });
  const hasCogs = !allZeroCols(cogsCols);
  let opsChildren = [revenueLabel];
  if (hasCogs) {
    const cogsLabel = emit(is.sections.cogs, 'Cost of revenue', 'Total cost of revenue',
                           { current: is.totals.totalCOGS, prior: is.prior.totalCOGS });
    rows.push({ k: 'total', label: 'Gross profit', level: 1,
                of: [revenueLabel, { label: cogsLabel, sign: -1 }],
                v: col({ current: is.totals.grossProfit,
                         prior: is.prior.grossProfit }) });
    opsChildren = ['Gross profit'];
  }

  const opexLabel = emit(is.sections.opex, 'Operating expenses',
                         'Total operating expenses',
                         { current: is.totals.totalOpEx, prior: is.prior.totalOpEx });
  if (opexLabel) opsChildren = [...opsChildren, { label: opexLabel, sign: -1 }];
  const opsLabel = 'Income (loss) from operations';
  rows.push({ k: 'total', label: opsLabel, level: 1, of: opsChildren,
              v: col({ current: is.totals.ebit, prior: is.prior.ebit }) });

  const otherItems = [
    ...((is.sections.otherIncome?.items) || []).map((it) => ({ it, sign: 1 })),
    ...((is.sections.otherExpense?.items) || []).map((it) => ({ it, sign: -1 })),
  ];
  const niChildren = [opsLabel];
  if (otherItems.length) {
    // #5 — parentheses and a slash inside a letterspaced uppercase heading do
    // not read well at any tracking value.
    rows.push({ k: 'section', label: 'Other income and expense' });
    otherItems.forEach(({ it, sign }) => {
      const leaves = it.kind === 'group' ? (it.leaves || []) : [it];
      leaves.forEach((l) => {
        rows.push({
          k: 'item', label: T(l.label), level: 4,
          v: col({ current: sign * (l.current || 0), prior: sign * (l.prior || 0) }),
        });
        niChildren.push(T(l.label));
      });
    });
  }
  // #7a — every total now declares its children, so the rounding
  // reconciliation and the validation tree walk both reach them.
  rows.push({ k: 'grand', label: niLabel, level: 1, strength: 'final',
              of: niChildren,
              v: col({ current: is.totals.netIncome, prior: is.prior.netIncome }) });
  return dropEmptySections(uniqueLabels(rows));
}

// ---------------------------------------------------------------- cash flow
export function cfRows(cf) {
  const rows = [];
  const one = (v) => [Math.round(v || 0)];
  const section = (heading, sec, totalLabelFor) => {
    const src = sec?.rows || [];
    if (!src.length && !sec?.subtotal) return null;
    rows.push({ k: 'section', label: heading });
    const kids = [];
    // #2d — a zero change in a balance is not a cash flow. Drop zero rows,
    // then drop any subheader left with nothing beneath it.
    const kept = src.filter((r) => r.subheader || Math.round(r.value || 0) !== 0);
    const live = kept.filter((r, i) => !r.subheader
      || kept.slice(i + 1).findIndex((n) => !n.subheader) === 0);
    // V25.17 — net income leads the operating section; the builder orders its
    // rows by magnitude, which put the depreciation add-back above it.
    live.sort((x, y) => (/^net income/i.test(x.label) ? -1 : 0)
                      - (/^net income/i.test(y.label) ? -1 : 0));
    live.forEach((r) => {
      if (r.subheader) {
        // the subheaders are the builder's own prose, already cased
        rows.push({ k: 'group', label: r.label, level: 3 });
        return;
      }
      rows.push({ k: 'item', label: cfLabel(r.label), level: 4, v: one(r.value) });
      kids.push(cfLabel(r.label));
    });
    const label = totalLabelFor(sec?.subtotal || 0);
    rows.push({ k: 'total', label, level: 1, of: kids, v: one(sec?.subtotal || 0) });
    return label;
  };

  const kids = [];
  const op = section('Cash flows from operating activities', cf.sections.operating,
                     (v) => v >= 0 ? 'Net cash provided by operating activities'
                                   : 'Net cash used in operating activities');
  if (op) kids.push(op);
  const inv = section('Cash flows from investing activities', cf.sections.investing,
                      (v) => v >= 0 ? 'Net cash provided by investing activities'
                                    : 'Net cash used in investing activities');
  if (inv) kids.push(inv);
  const fin = section('Cash flows from financing activities', cf.sections.financing,
                      (v) => v >= 0 ? 'Net cash provided by financing activities'
                                    : 'Net cash used in financing activities');
  if (fin) kids.push(fin);

  const changeLabel = (cf.netChange || 0) >= 0 ? 'Net increase in cash'
                                               : 'Net decrease in cash';
  rows.push({ k: 'total', label: changeLabel, level: 1, of: kids,
              v: one(cf.netChange) });
  rows.push({ k: 'item', label: 'Cash, beginning of period', level: 1,
              v: one(cf.beginCash) });
  rows.push({ k: 'grand', label: 'Cash, end of period', level: 1, strength: 'final',
              of: [changeLabel, 'Cash, beginning of period'], v: one(cf.endCash) });
  return dropEmptySections(uniqueLabels(rows));
}

// ---------------------------------------------------------------- TTM
function ttmSpec(t12, months, cogsHeading) {
  const rows = [];
  const controls = {};
  const formulas = [];
  const sectionLabel = (label) => (
    /cost of goods sold|cost of revenue/i.test(label) ? cogsHeading : label
  );
  // Positive / negative ITEM labels in statement order. The builder tags each
  // section with sign:'neg', so every computed row can be rebuilt from the
  // printed detail lines without guessing at section names.
  const pos = [];
  const neg = [];
  const revenueLabels = [];
  let niLabel = null;

  (t12.sections || []).forEach((s) => {
    if (s.isMargin) {
      rows.push({ kind: 'margin', label: 'Net margin', values: s.monthsTotal,
                  total: s.total });
      if (niLabel && revenueLabels.length) {
        formulas.push({ label: 'Net margin',
                        percent_of: { of: niLabel, base: revenueLabels } });
      }
      return;
    }
    if (s.isSubtotal) {
      const isNet = /net income/i.test(s.label);
      const label = isNet ? 'Net income' : 'Gross profit';
      rows.push({ kind: isNet ? 'grand' : 'total', label, values: s.monthsTotal,
                  total: s.total });
      formulas.push({ label, add: [...pos], subtract: [...neg] });
      if (isNet) niLabel = label;
      return;
    }
    const isNeg = s.sign === 'neg';
    const heading = sectionLabel(s.label);
    rows.push({ kind: 'section', label: heading });
    const sectionItems = [];
    (s.rows || []).forEach((r) => {
      // V25.17 — a row with no activity in any of the twelve months is not
      // information. Dropping it here keeps it out of the formulas and the
      // controls as well, so every computed row still foots.
      const empty = (r.months || []).every(
        (v) => typeof v !== 'number' || Math.round(v) === 0)
        && (!Number.isFinite(r.total) || Math.round(r.total) === 0);
      if (empty) return;
      const label = T(r.label);
      rows.push({ kind: 'item', label, values: r.months });
      if (Number.isFinite(r.total)) controls[label] = r.total;
      sectionItems.push(label);
      (isNeg ? neg : pos).push(label);
      if (/revenue|sales/i.test(s.label) && !isNeg) revenueLabels.push(label);
    });
    if (/operating expense/i.test(s.label)) {
      rows.push({ kind: 'total', label: 'Total operating expenses',
                  values: s.monthsTotal, total: s.total });
      formulas.push({ label: 'Total operating expenses', add: sectionItems,
                      subtract: [] });
    }
  });

  const spec = {
    title: 'Trailing 12 Months Statement of Income',
    period: `Trailing twelve months ${months[0]} \u2013 ${months[months.length - 1]}`,
    months, controls, formulas,
    // #2a — the landscape TTM renderer must behave like the portrait one:
    // no heading survives with nothing under it.
    rows: dropEmptySections(rows, 'kind'),
  };
  if (niLabel) {
    spec.net_income_of = { label: niLabel, add: [...pos], subtract: [...neg] };
  }
  return spec;
}

// ---------------------------------------------------------------- payload
export function buildPackagePayload({ client, bs, is, isMonth = null, cf, t12,
                                      months = [], periodLabels,
                                      statementTitles = {} }) {
  const entity = client?.company || client?.name || 'Financial Statements';
  const niLabel = 'Net income (loss)';
  const bsNiLabel = 'Current year earnings';
  const asOf = periodLabels.asOfLabel;

  const one = (l) => [Math.round(l.current || 0)];
  const cmp = (l) => {
    const a = Math.round(l.current || 0);
    const b = Math.round(l.prior || 0);
    return [a, b, a - b, PCT_CHANGE(a, b)];
  };
  const revenue = is.totals.totalRevenue || 0;
  const onePct = (l) => {
    const a = Math.round(l.current || 0);
    return [a, PCT_REV(a, revenue)];
  };

  const hasPrior = Math.abs(bs.prior?.totalAssets || 0) > 0
    || Math.abs(is.prior?.totalRevenue || 0) > 0;

  // V25.16 — the reporting MONTH beside the year-to-date column. A package
  // whose period IS a single month already prints that month, so the second
  // pair of columns is only added when the two differ.
  const periodEnd = periodLabels.periodEnd instanceof Date
    ? periodLabels.periodEnd : null;
  const hasMonth = !!isMonth && (periodLabels.monthsCount || 0) > 1 && !!periodEnd;
  const monthName = periodEnd ? MONTH_NAMES[periodEnd.getMonth()] : '';
  const asOfHead = periodEnd
    ? `${monthName.toUpperCase()} ${periodEnd.getDate()}, ${periodEnd.getFullYear()}`
    : 'AMOUNT';
  const monthHead = periodEnd
    ? `${monthName.toUpperCase()} ${periodEnd.getFullYear()}` : 'MONTH';
  // #4 — "For the Month and Eight Months Ended August 31, 2026".
  const forPeriod = hasMonth
    ? `For the Month and ${periodLabels.monthsEndedLabel}`
    : `For the ${periodLabels.monthsEndedLabel}`;
  const cfPeriod = `For the ${periodLabels.monthsEndedLabel}`;

  // The month figures come from the same builder over the month's own P&L, so
  // they are zipped onto the year-to-date row skeleton by label. A row the
  // month statement suppressed contributes zero, which is what it earned.
  const monthByLabel = new Map();
  if (hasMonth) {
    isRows(isMonth, (l) => [Math.round(l.current || 0), Math.round(l.prior || 0)],
           niLabel)
      .forEach((r) => { if (r.v) monthByLabel.set(r.label, r.v); });
  }
  const mv = (label, i) => (monthByLabel.get(label) || [0, 0])[i] || 0;
  const monthRevenue = hasMonth ? (isMonth.totals.totalRevenue || 0) : 0;

  const bsSingle = dropZeroLines(bsRows(bs, bsNiLabel, one));
  const isSingle = dropEmptySections(dropZeroLines(hasMonth
    ? isRows(is, one, niLabel).map((r) => (r.v ? { ...r, v: [
        mv(r.label, 0),
        PCT_REV(mv(r.label, 0), monthRevenue),
        r.v[0],
        PCT_REV(r.v[0], revenue),
      ] } : r))
    : isRows(is, onePct, niLabel)));
  const cfSingle = cfRows(cf);
  const bsComp = hasPrior ? dropZeroLines(bsRows(bs, bsNiLabel, cmp)) : null;
  const isComp = hasPrior
    ? dropEmptySections(dropZeroLines(
        isRows(is, cmp, niLabel).map((r) => (r.v && hasMonth ? { ...r, v: [
          mv(r.label, 0), mv(r.label, 1), r.v[0], r.v[1], r.v[2], r.v[3],
        ] } : r))))
    : null;
  // Column index of the year-to-date figures on each income statement: the
  // ties and the equity roll-forward read the YTD column, never the month.
  const isYtdCol = hasMonth ? 2 : 0;
  const compCur = hasMonth ? 2 : 0;
  const compPri = hasMonth ? 3 : 1;

  const statements = [
    { key: 'balance_sheet', toc: 'Balance sheet',
      title: statementTitles.balance_sheet || 'Balance Sheet',
      period: asOf, col_heads: [asOfHead], colw: 78, rule_scope: 'numeric',
      // V25.24 — no column inset anywhere. A single-column statement used to
      // be inset 1.15" from the right margin while a multi-column one sat
      // flush against it, so the Balance Sheet and the Statement of Cash
      // Flows printed their figures 83pt left of the Statement of Income's —
      // visibly out of alignment when scrolling the package.
      rows: bsSingle },
    { key: 'income', toc: 'Statement of income',
      title: statementTitles.income_statement || 'Statement of Income',
      period: forPeriod,
      col_heads: hasMonth ? [monthHead, '% REV', 'YEAR TO DATE', '% REV']
                          : ['YEAR TO DATE', '% REV'],
      sec_cols: hasMonth ? [1, 3] : [1],
      colw: hasMonth ? 66 : 74, rows: isSingle },
    { key: 'cashflow', toc: 'Statement of cash flows',
      title: statementTitles.cash_flow || 'Statement of Cash Flows',
      period: `${cfPeriod}  \u00b7  Indirect Method`, col_heads: ['YEAR TO DATE'],
      colw: 78, rows: cfSingle },
  ];
  // V25.17 — one quiet footnote wherever a percentage had to say `n/m`.
  const withNmNote = (rows) => (
    rows.some((r) => (r.v || []).some((v) => v === 'n/m'))
      ? [...rows, { k: 'note', label: 'n/m: not meaningful' }]
      : rows);
  if (hasPrior) {
    statements.push({
      key: 'bs_comp', toc: 'Balance sheet \u2014 prior year comparison',
      title: `${statements[0].title} \u2014 Prior Year Comparison`,
      period: `${asOf} and ${periodLabels.priorYear}`,
      col_heads: [String(periodLabels.reportingYear), String(periodLabels.priorYear),
                  'CHANGE', '% CHG'],
      sec_cols: [2, 3], colw: 70, rule_scope: 'numeric', comparative: true,
      // CHANGE / % CHG are derived: the server rebuilds them from the two
      // balance columns after rounding reconciliation
      derived: { base: [0, 1], change_col: 2, pct_col: 3 },
      rows: withNmNote(bsComp),
    });
    statements.push({
      key: 'inc_comp', toc: 'Statement of income \u2014 prior year comparison',
      title: `${statements[1].title} \u2014 Prior Year Comparison`,
      period: `${forPeriod} and ${periodLabels.priorYear}`,
      col_heads: hasMonth
        ? [`${monthName.slice(0, 3).toUpperCase()} ${periodLabels.reportingYear}`,
           `${monthName.slice(0, 3).toUpperCase()} ${periodLabels.priorYear}`,
           `YTD ${periodLabels.reportingYear}`, `YTD ${periodLabels.priorYear}`,
           'CHANGE', '% CHG']
        : [String(periodLabels.reportingYear), String(periodLabels.priorYear),
           'CHANGE', '% CHG'],
      sec_cols: hasMonth ? [4, 5] : [2, 3],
      colw: hasMonth ? 52 : 70, comparative: true,
      // CHANGE / % CHG are always derived from the two YEAR-TO-DATE columns;
      // the month pair stands on its own.
      derived: hasMonth
        ? { base: [2, 3], change_col: 4, pct_col: 5 }
        : { base: [0, 1], change_col: 2, pct_col: 3 },
      rows: withNmNote(isComp),
    });
  }

  const ttm = (t12 && months.length === 12)
    ? ttmSpec(t12, months, 'Cost of revenue')
    : null;

  // ---- ties: only declared when both sides actually printed ----
  const ties = [];
  const tie = (group, name, label, left, right, sign, offset, blocking = true,
               compare = null) => {
    const l = statements.find((s) => s.key === left[0]);
    const r = statements.find((s) => s.key === right[0]);
    if (!l || !r || !hasLabel(l.rows, left[1]) || !hasLabel(r.rows, right[1])) return;
    ties.push({
      group, name, label,
      left: { statement: left[0], label: left[1], col: left[2] },
      right: { statement: right[0], label: right[1], col: right[2] },
      ...(sign ? { sign } : {}),
      ...(compare ? { compare } : {}),
      ...(offset ? { offset, offset_label: 'opening balance' } : {}),
      ...(blocking ? {} : { blocking: false }),
    });
  };
  tie('equation', 'Balance Sheet', 'total assets vs. total liabilities and equity',
      ['balance_sheet', 'Total assets', 0],
      ['balance_sheet', "Total liabilities and stockholders' equity", 0]);
  if (hasPrior) {
    tie('equation', 'Balance Sheet - Prior Year',
        'prior-year total assets vs. total liabilities and equity',
        ['bs_comp', 'Total assets', 1],
        ['bs_comp', "Total liabilities and stockholders' equity", 1]);
  }
  tie('equation', 'Cross-statement',
      'net income vs. current year earnings on the balance sheet',
      ['income', niLabel, isYtdCol], ['balance_sheet', bsNiLabel, 0]);

  // V25.26 — the tie anchors on the row that ACTUALLY PRINTED, never on the
  // pre-suppression account list. Elite Home held one funded bank account and
  // a zero Undeposited Funds: the emitter saw two cash lines and pointed the
  // tie at 'Total cash & equivalents', zero-line suppression then dropped the
  // empty account, the single-child subgroup collapsed, and the tie lost its
  // anchor — which refused the whole package. Reading the built rows also
  // removes a casing mismatch, since printed labels are title-cased.
  const cashSubtotal = bsSingle.find(
    (r) => r.k === 'subtotal' && /^total cash/i.test(r.label));
  const cashLines = bs.sections.cash?.lines || bs.sections.cash?.items || [];
  const cashNames = new Set(cashLines.map((l) => T(l.label)));
  const cashItem = bsSingle.find(
    (r) => r.k === 'item' && r.v && cashNames.has(r.label));
  const cashLabel = cashSubtotal?.label || cashItem?.label
    || 'Total cash & equivalents';
  tie('equation', 'Cross-statement', 'ending cash vs. cash on the balance sheet',
      ['cashflow', 'Cash, end of period', 0], ['balance_sheet', cashLabel, 0]);

  const cfNi = cfSingle.find((r) => r.k === 'item' && /^net income/i.test(r.label));
  if (cfNi) {
    tie('equation', 'Cross-statement',
        'cash flow net income vs. statement of income',
        ['cashflow', cfNi.label, 0], ['income', niLabel, isYtdCol]);
  }

  const cfDep = cfSingle.filter((r) => r.k === 'item' && /deprecia|amortiz/i.test(r.label));
  const isDep = isSingle.filter((r) => r.k === 'item' && /deprecia/i.test(r.label));
  if (cfDep.length === 1 && isDep.length === 1) {
    // V25.15 — MAGNITUDE comparison, not a signed identity.
    //
    // Reported on MEET Ministry: "depreciation expense vs. the cash flow
    // add-back - 73,657 vs -73,657 (differs by +147,314)". The presentation
    // sign of the expense line and of the operating add-back is a convention,
    // not arithmetic: some engagements print expenses positive within an
    // expense section, others print them negative as a deduction, and the
    // cash-flow add-back is always presented as an increase to cash. Only the
    // AMOUNT is an identity, so only the amount is gated.
    tie('cross', 'Depreciation', 'depreciation expense vs. the cash flow add-back',
        ['income', isDep[0].label, isYtdCol], ['cashflow', cfDep[0].label, 0],
        null, 0, true, 'magnitude');
  }
  const cfDraw = cfSingle.filter((r) => r.k === 'item' && /draw|distribut/i.test(r.label));
  const bsDraw = bsSingle.filter((r) => r.role === 'distributions');
  if (cfDraw.length === 1 && bsDraw.length === 1) {
    // V25.14 — ADVISORY, not render-blocking.
    //
    // The balance-sheet draws line and the cash-flow draw line are not a
    // mathematical identity. They diverge legitimately when the account was
    // never closed to retained earnings, when the reporting period starts
    // after the fiscal year (draws booked before the period are in the
    // balance but not in the period's cash flow), or when a distribution was
    // non-cash. Elite Home & Commercial hit two of these in a row. A
    // render-blocking gate may only contain identities that hold in EVERY
    // configuration; this one does not, so it reports and lets the preparer
    // judge. The identities that do always hold (the accounting equation,
    // ending cash vs. balance-sheet cash, net income across statements)
    // remain blocking.
    tie('cross', 'Distributions',
        'owner draws on the balance sheet vs. the cash flow draw line',
        ['balance_sheet', bsDraw[0].label, 0], ['cashflow', cfDraw[0].label, 0],
        1, bsDraw[0]._prior || 0, false);
  }

  const equity_checks = [{
    statement: 'balance_sheet', total_label: "Total stockholders' equity",
    columns: [{ col: 0,
                net_income: { statement: 'income', label: niLabel, col: isYtdCol } }],
  }];
  if (hasPrior) {
    equity_checks.push({
      statement: 'bs_comp', total_label: "Total stockholders' equity",
      columns: [
        { col: 0, net_income: { statement: 'inc_comp', label: niLabel, col: compCur } },
        { col: 1, net_income: { statement: 'inc_comp', label: niLabel, col: compPri } },
      ],
    });
  }

  const payload = {
    entity,
    // V25.10 — stamped so a stale browser bundle is impossible to mistake for
    // a renderer bug: the gate report prints this next to the renderer version.
    emitter_version: EMITTER_VERSION,
    period_label: periodLabels.monthsEndedLabel,
    // V25.25 — the engagement window in ISO form. The cover spells it out and
    // the download filename carries it, so a folder of packages is
    // self-identifying and sorts by period.
    period_start: isoDay(periodLabels.periodStart),
    period_end: isoDay(periodLabels.periodEnd),
    statements,
    ttm,
    ties,
    equity_checks,
  };
  return client?.entity_type === 'nonprofit'
    ? applyNonprofitWording(payload) : payload;
}

export default buildPackagePayload;
